/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {Input} from "pages/Front/Booking/components/BookingInput";
import {useTranslation} from "react-i18next";
import {ErrorOption, FieldPath} from "react-hook-form";
import {VerificationType} from "types/enums/VerificationType";
import {toast} from "react-toastify";
import clsx from "clsx";
import {PaperAirplaneIcon} from "@heroicons/react/24/outline";
import {useRequiredField, useValueVerification} from "utils/bookingUtils";
import {emailValidation} from "utils/utility";
import {ObjectSchema} from "yup";
import {VerificationStatusResponse} from "types/responses/VerificationStatusResponse";
import {CompanyInputs} from "types/entities/specific/CompanyInputs";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import Lottie from "lottie-react";
import SuccessAnimation from "components/Animations/SuccessAnimation.json";
import {StrongText} from "pages/Front/components/TextStyles/StrongText";
import BookingAction from "store/booking/BookingAction";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import {DEFAULT_HALL_ID} from "helpers/config";
import {useDebouncedRequest} from "utils/useDebouncedRequest";
import {PackagesCart} from "types/entities/PackagesCart";

const LoginCardForShop = (props: {
    clearErrors: any;
    errors: any;
    register: any;
    defaultValues: any;
    schema: ObjectSchema<any>;
    setValue: (name, value, options?: {shouldValidate: boolean}) => void;
    setError: (name: FieldPath<CompanyInputs>, error: ErrorOption, options?: {shouldFocus: boolean}) => void;
}) => {
    const emailLogin = useTypedSelector(state => state.bookingState.bookingProcess.emailLogin);
    const packagesCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [isMailSubmitted, setIsMailSubmitted] = useState(false);
    const [resultVerification, setResultVerification] = useState<VerificationStatusResponse | null>(null);

    const [verificationCode, setVerificationCode] = useState<number | null>(null);
    const [responseCompanyPresent, setResponseCompanyPresent] = useState<boolean>(false);
    const [loginValue, setLoginValue] = useState<string>("");
    const [proceedToLogin, setProceedToLogin] = useState<boolean>(false);
    const valueVerification = useValueVerification(packagesCart as PackagesCart);
    const requiredField = useRequiredField(props.schema);
    const {debouncedRequest} = useDebouncedRequest({delay: 1000});

    const submitBackgroundLoginHandler = async () => {
        const resultVerificationResponse = await debouncedRequest(
            BookingAction.fetchVerificationForLogin({
                value: loginValue,
                hall_id: DEFAULT_HALL_ID,
                type: VerificationType.Login,
            })
        );
        // console.log("result verification response", resultVerificationResponse);
        if (resultVerificationResponse.responseCompany != null && resultVerificationResponse.responseCompany !== false) {
            setResponseCompanyPresent(true);
        }

        if (resultVerificationResponse.responseCompany == false) {
            dispatch(BookingAction.setLoginVerified(true));
        }
        //set this and allow the rest of form to be filled in
        // dispatch(BookingAction.setLoginVerified(true));

        return resultVerificationResponse;
    };

    const setLoginValueHandler = (email: string) => {
        setLoginValue(email);
        if (emailLogin) {
            dispatch(BookingAction.setLoginVerified(false));
        }
        setIsMailSubmitted(false);
        props.setValue("email", email, {shouldValidate: true});
    };

    const setVerificationCodeHandler = (value: number) => {
        setVerificationCode(value);
        if (value.toString().length === 4) {
            submitCodeHandler(value).then(r => {
                if (r) {
                    dispatch(BookingAction.setLoginVerified(true));
                    setIsMailSubmitted(true);
                } else {
                    dispatch(BookingAction.setLoginVerified(false));
                    setIsMailSubmitted(false);
                }
            });
        }
    };
    const submitCodeHandler = async (verificationCode: number) => {
        // const data = e.target.name;
        if (!resultVerification || !verificationCode) {
            toast.error(t("Submit email for verification first"));
            return;
        }

        const data = {
            request_id: resultVerification.verification_request_id,
            type: VerificationType.Login,
            verification_code: "" + verificationCode,
        };
        // data_2: {request_id: string; verification_code: string; type: VerificationType},
        const handleVerificationResponse = await valueVerification.handleVerification(data, resultVerification);
        if (handleVerificationResponse.company) {
            dispatch(BookingAction.setCustomer(handleVerificationResponse.company));
        }
        return handleVerificationResponse;
    };

    const validateEmailForLoginHandler = (loginValue: string) => {
        const isValidated = emailValidation(loginValue);

        if (!loginValue) {
            props.setError(
                "email",
                {
                    type: "error",
                    message: "Email is required",
                },
                {shouldFocus: true}
            );
            return Promise.reject("Email is required");
        }

        if (!isValidated) {
            props.setError(
                "email",
                {
                    type: "error",
                    message: "Invalid email format",
                },
                {shouldFocus: true}
            );
            return Promise.reject("Invalid email format!");
        }

        return Promise.resolve(true);
    };

    const submitEmailLoginHandler = async () => {
        const resultVerificationResponse = await debouncedRequest(
            BookingAction.fetchVerificationForShop({
                value: loginValue,
                hall_id: DEFAULT_HALL_ID,
                type: VerificationType.Email,
            })
        );

        if (resultVerificationResponse) {
            setResultVerification(resultVerificationResponse);
            // console.log("result verification response", resultVerificationResponse);
            if (resultVerificationResponse.status == 1) {
                setProceedToLogin(true);
            }
            // console.log("result verification response", resultVerificationResponse);
        }
    };

    const skipLoginHandler = async () => {
        // console.log("skip login");
        setResponseCompanyPresent(false);
        dispatch(BookingAction.setLoginVerified(true));
    };

    const submitLoginHandler = async () => {
        await safeValidationForEmail(loginValue);
    };

    const safeValidationForEmail = async (loginValue: string) => {
        try {
            const r = await validateEmailForLoginHandler(loginValue);
            if (r) {
                await submitBackgroundLoginHandler();
            }
        } catch (e) {
            // console.log(e);
        }
    };

    useEffect(() => {
        setLoginValueHandler(props.defaultValues.email);
    }, []);

    useEffect(() => {
        if (resultVerification && resultVerification.status === 2) {
            dispatch(BookingAction.setEmailVerified(true));
            setIsMailSubmitted(true);
        }
    }, [resultVerification]);

    useEffect(() => {
        if (loginValue) {
            safeValidationForEmail(loginValue);
        }
    }, [loginValue]);

    const [value, setValue] = useState(0);

    // useEffect(() => {
    //     if (value >= 100) {
    //         // props.onFinish();
    //         submitEmailLoginHandler();
    //         return;
    //     }
    //
    //     const interval = setInterval(() => {
    //         if (!responseCompanyPresent) {
    //             return;
    //         }
    //         if (value >= 100) {
    //             // props.onFinish();
    //             clearInterval(interval);
    //             submitEmailLoginHandler();
    //             return;
    //         }
    //         setValue(value => value + 1);
    //     }, 50);
    //     return () => clearInterval(interval);
    // }, [value, responseCompanyPresent]);

    return (
        <div className={"LoginCardForShop basis-[50%] flex-shrink-1 flex-grow-1 flex flex-col space-y-6"}>
            <CardComponent>
                <Input
                    className={"w-full pl-10"}
                    disabled={isMailSubmitted}
                    errors={props.errors}
                    label={t("E-mail")}
                    name={"email"}
                    register={props.register}
                    required={requiredField.testField("email")}
                    type={"email"}
                    onChange={e => setLoginValueHandler(e.target.value)}
                    onEnter={e => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            submitEmailLoginHandler();
                        }
                    }}
                />
                {responseCompanyPresent && !proceedToLogin && (
                    <div className={"mt-4"}>
                        <div className={"flex flex-col justify-center text-center items-center"}>
                            <div className={"text-sm text-gray-600 mb-2 leading-4"}>
                                {t("This e-mail address is already registered. Please log in to continue.")}
                            </div>
                            <button
                                className={clsx(
                                    "bg-cta-800 text-white px-3 py-3 rounded-md text-md flex items-center place-content-center w-full min-w-[300px]",
                                    "hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 relative"
                                )}
                                type="button"
                                onClick={submitEmailLoginHandler}
                            >
                                <PaperAirplaneIcon className={"size-5 mr-2"} />
                                <span>{t("Login")}</span>
                                {/*<div*/}
                                {/*    className={"absolute h-full left-0 bg-white/10"}*/}
                                {/*    style={{*/}
                                {/*        width: value + "%",*/}
                                {/*    }}*/}
                                {/*/>*/}
                            </button>
                            {/*<hr className={"w-full mt-2 mb-2 h-px bg-gray-200"} />*/}
                            {/*<button*/}
                            {/*    className={clsx(*/}
                            {/*        "text-primary-800  bg-gray-200 flex place-content-center items-center rounded-md px-3 py-2 text-xs w-full min-w-[300px]",*/}
                            {/*        "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"*/}
                            {/*    )}*/}
                            {/*    type="button"*/}
                            {/*    onClick={skipLoginHandler}*/}
                            {/*>*/}
                            {/*    <UserPlusIcon className={"text-gray-600 size-4 mr-2"} />*/}
                            {/*    <span>{t("Create new account")}</span>*/}
                            {/*</button>*/}
                        </div>
                    </div>
                )}
                {!emailLogin ? (
                    <div className={clsx(proceedToLogin ? "" : "hidden")}>
                        {/*<button*/}
                        {/*    className={clsx(*/}
                        {/*        !isMailSubmitted ? "bg-cta-800" : "bg-primary-800 disabled",*/}
                        {/*        "mt-3 inline-flex w-full items-center justify-center rounded-md px-3 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"*/}
                        {/*    )}*/}
                        {/*    disabled={isMailSubmitted}*/}
                        {/*    type="button"*/}
                        {/*    onClick={submitLoginHandler}*/}
                        {/*>*/}
                        {/*    <PaperAirplaneIcon className={"w-5 h-5 mr-2"} />*/}
                        {/*    <span className={"first-letter:uppercase"}>{!isMailSubmitted ? t("verify") : t("Enter verification code")}</span>*/}
                        {/*</button>*/}

                        <div className="w-full mt-2 flex flex-col justify-center items-center">
                            <label className="sr-only" htmlFor="verification_code">
                                Verification Code
                            </label>
                            <input
                                className="block w-full rounded-md border py-3 text-center text-gray-900 shadow-sm active:ring-cta-800 active:ring-2 active:ring-offset-2 focus:border-cta-800 checked:bg-cta-800 sm:text-sm border-gray-800 placeholder:text-gray-800 sm:leading-6"
                                id="verification_code"
                                maxLength={16}
                                minLength={4}
                                name={"verification_code"}
                                placeholder="* * * *"
                                type="text"
                                value={verificationCode ?? ""}
                                onChange={e => setVerificationCodeHandler(+e.target.value)}
                                onKeyPress={e => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                    }
                                }}
                            />
                            <div className={"font-medium mt-4 text-sm px-10 text-gray-800 mb-2 leading-4 text-center"}>
                                {t("enter_code_via", {type: "E-mail"})}
                            </div>
                            <div className={"flex flex-row gap-x-3 justify-around my-1 border-t border-gray-200 pt-4 w-full"}>
                                <button
                                    className={clsx("text-primary-800 flex place-content-center items-center rounded-md px-3 py-2 text-xs")}
                                    type="button"
                                    onClick={submitEmailLoginHandler}
                                >
                                    <PaperAirplaneIcon className={"size-5 mr-2"} />
                                    <span>{t("Send code again")}</span>
                                </button>
                                {/*<button*/}
                                {/*    className={clsx("text-primary-800 flex place-content-center items-center rounded-md px-3 py-2 text-xs")}*/}
                                {/*    type="button"*/}
                                {/*    onClick={skipLoginHandler}*/}
                                {/*>*/}
                                {/*    <UserPlusIcon className={"text-gray-600 size-4 mr-2"} />*/}
                                {/*    <span>{t("Create new account")}</span>*/}
                                {/*</button>*/}
                            </div>
                            <hr className={"w-full mt-2 mb-2 h-px bg-gray-200"} />
                            <div className={"mt-2 italic text-xs text-gray-600 mb-2 text-center leading-4"}>
                                {t("Please check your spam / junk box if you haven't received the email.")}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={clsx(responseCompanyPresent ? "" : "hidden", "mt-4 ")}>
                        <div className={"flex flex-row justify-start items-center space-x-4 text-sm sm:text-base"}>
                            <div className={"w-10 h-10 rounded-full shadow-lg p-1 bg-green-100"}>
                                <Lottie animationData={SuccessAnimation} loop={false} onComplete={() => {}} />
                            </div>
                            <StrongText text={t("Your email has been verified successfully")} />
                        </div>
                    </div>
                )}
            </CardComponent>
        </div>
    );
};

export default LoginCardForShop;
